import React from "react";
import { Modal2 } from "@iq/models";
import Input from "../../modelsTesting/Input/Input";
import { LuImagePlus, LuX } from "react-icons/lu";
import {
    getProductVariantQuickStats,
    getProductVariantImages,
    deleteProductVariant,
    deleteProductImage,
    getProductVariants,
    updateProductVariantAttribute,
    updateProductVariantPrice,
    uploadProductImage,
} from "../../services/productService";
import "./variantModal.css";
import { responsiveMedia } from "../../services/media";

const formatCurrency = (cents) => {
    const safeCents = Number(cents ?? 0);
    if (!Number.isFinite(safeCents)) return "$0.00";
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    }).format(safeCents / 100);
};

const resolveVariantId = (variant) => Number(
    variant?.id ?? variant?.ID ?? variant?.variant_id ?? variant?.variantId ?? 0
);

const getPriceCents = (variant) => Number(
    variant?.price?.cents ?? variant?.Price ?? variant?.price ?? 0
);

const toAttributeDrafts = (attributes) => (Array.isArray(attributes) ? attributes : []).map((attribute, index) => ({
    id: attribute?.id ?? attribute?.ID ?? attribute?.variant_attribute_id ?? 0,
    name: attribute?.name ?? attribute?.Name ?? "",
    value: attribute?.value ?? attribute?.Value ?? "",
    colorCode: attribute?.color_code ?? attribute?.colorCode ?? attribute?.ColorCode ?? "",
    clientId: `attr-${attribute?.id ?? attribute?.ID ?? attribute?.variant_attribute_id ?? "x"}-${index}`,
}));

const normalizeImage = (image) => ({
    id: Number(image?.id ?? image?.ID ?? 0),
    url: image?.url ?? image?.URL ?? "",
    alt_text: image?.alt_text ?? image?.altText ?? image?.AltText ?? "",
});

/**
 * Price, attribute values, and photo edits remain local until Save Changes.
 * Closing discards unsaved edits and releases their preview URLs.
 */
export default function VariantModal({ isOpen, variant, onClose, onVariantUpdated }) {
    const [stats, setStats] = React.useState(null);
    const [attributeDrafts, setAttributeDrafts] = React.useState([]);
    const [savedAttributes, setSavedAttributes] = React.useState([]);
    const [priceDraft, setPriceDraft] = React.useState("");
    const [savedPriceCents, setSavedPriceCents] = React.useState(0);
    const [galleryImages, setGalleryImages] = React.useState([]);
    const [savedImages, setSavedImages] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [loadError, setLoadError] = React.useState("");
    const [saveError, setSaveError] = React.useState("");
    const fileInputRef = React.useRef(null);
    const previewUrls = React.useRef(new Set());
    const nextImageId = React.useRef(0);
    const uploadInputId = React.useId();

    const productId = Number(variant?.product_id ?? variant?.productId ?? 0);
    const variantId = resolveVariantId(variant);
    const busy = isSaving || isDeleting;

    const clearPreviews = React.useCallback(() => {
        previewUrls.current.forEach((url) => URL.revokeObjectURL(url));
        previewUrls.current.clear();
    }, []);

    React.useEffect(() => clearPreviews, [clearPreviews]);

    React.useEffect(() => {
        let mounted = true;
        clearPreviews();
        setStats(null);
        setAttributeDrafts([]);
        setSavedAttributes([]);
        setGalleryImages([]);
        setSavedImages([]);
        setPriceDraft("");
        setSavedPriceCents(0);
        setLoadError("");
        setSaveError("");
        if (!isOpen || !variantId) {
            setIsLoading(false);
            return undefined;
        }

        setIsLoading(true);
        async function loadVariant() {
            try {
                const [quickStats, variantsPayload, imagesPayload] = await Promise.all([
                    getProductVariantQuickStats(variantId),
                    productId ? getProductVariants(productId) : Promise.resolve({ variants: [] }),
                    getProductVariantImages(variantId),
                ]);
                if (!mounted) return;

                const list = Array.isArray(variantsPayload?.variants) ? variantsPayload.variants : [];
                const details = list.find((candidate) => resolveVariantId(candidate) === variantId)
                    || list.find((candidate) => (
                        String(candidate?.sku ?? candidate?.SKU ?? "").toLowerCase()
                        === String(variant?.sku?.value ?? variant?.SKU ?? variant?.sku ?? "").toLowerCase()
                    ))
                    || variant;
                const attributes = toAttributeDrafts(Array.isArray(details?.attributes) ? details.attributes : variant?.attributes);
                const images = (Array.isArray(imagesPayload?.images) ? imagesPayload.images : [])
                    .map(normalizeImage).filter((image) => image.url)
                    .map((image) => ({ ...image, clientId: `saved-${image.id}` }));
                const price = getPriceCents(details);
                setStats(quickStats?.stats ?? quickStats ?? null);
                setAttributeDrafts(attributes);
                setSavedAttributes(attributes);
                setGalleryImages(images);
                setSavedImages(images);
                setPriceDraft((price / 100).toFixed(2));
                setSavedPriceCents(price);
            } catch (error) {
                if (mounted) setLoadError(error?.message || "Failed to load variant data.");
            } finally {
                if (mounted) setIsLoading(false);
            }
        }
        loadVariant();
        return () => { mounted = false; };
    }, [isOpen, variantId, productId, variant, clearPreviews]);

    const parsedPrice = Number(priceDraft);
    const draftPriceCents = Math.round(parsedPrice * 100);
    const priceChanged = priceDraft.trim() === "" || !Number.isFinite(parsedPrice) || parsedPrice !== savedPriceCents / 100;
    const changedAttributes = attributeDrafts.filter((attribute) => (
        String(attribute.value).trim() !== String(savedAttributes.find((saved) => saved.clientId === attribute.clientId)?.value ?? "").trim()
        || attribute.colorCode !== (savedAttributes.find((saved) => saved.clientId === attribute.clientId)?.colorCode ?? "")
    ));
    const addedImages = galleryImages.filter((image) => image.file);
    const deletedImages = savedImages.filter((image) => !galleryImages.some((draft) => draft.id === image.id));
    const hasChanges = priceChanged || changedAttributes.length > 0 || addedImages.length > 0 || deletedImages.length > 0;
    const editingDisabled = busy || isLoading || Boolean(loadError) || !variantId;

    const discardPreview = (image) => {
        if (previewUrls.current.delete(image.url)) URL.revokeObjectURL(image.url);
    };

    const createImageDraft = (file) => {
        const url = URL.createObjectURL(file);
        previewUrls.current.add(url);
        return { id: 0, clientId: `new-${nextImageId.current++}`, url, alt_text: file.name, file };
    };

    const handleSelectImages = (event) => {
        const files = Array.from(event.target.files ?? []);
        event.target.value = "";
        if (files.some((file) => !file.type.startsWith("image/"))) {
            setSaveError("Choose image files only.");
            return;
        }
        setSaveError("");
        const images = files.map(createImageDraft);
        setGalleryImages((current) => [...current, ...images]);
    };

    const handleReplaceImage = (image, event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            setSaveError("Choose an image file.");
            return;
        }
        const replacement = createImageDraft(file);
        discardPreview(image);
        setGalleryImages((current) => current.map((entry) => entry.clientId === image.clientId ? replacement : entry));
        setSaveError("");
    };

    const handleRemoveImage = (image) => {
        discardPreview(image);
        setGalleryImages((current) => current.filter((entry) => entry.clientId !== image.clientId));
    };

    const handleSaveChanges = async () => {
        if (editingDisabled || !hasChanges) return;
        if (!productId) {
            setSaveError("A valid product id is required to save variant changes.");
            return;
        }
        if (!/^\d+(?:\.\d{1,2})?$/.test(priceDraft.trim()) || !Number.isSafeInteger(draftPriceCents)) {
            setSaveError("Enter a valid non-negative price with up to two decimal places.");
            return;
        }
        if (changedAttributes.some((attribute) => !attribute.id || !String(attribute.name).trim() || !String(attribute.value).trim())) {
            setSaveError("Each changed attribute must have an existing id, name, and a non-empty value.");
            return;
        }
        if (deletedImages.some((image) => !image.id)) {
            setSaveError("A photo is missing its id. Reopen the variant before removing it.");
            return;
        }

        setIsSaving(true);
        setSaveError("");
        try {
            // Checkpoint each successful request so retries do not repeat saved changes.
            if (priceChanged) {
                await updateProductVariantPrice(variantId, draftPriceCents);
                setSavedPriceCents(draftPriceCents);
                setPriceDraft((draftPriceCents / 100).toFixed(2));
            }
            for (const attribute of changedAttributes) {
                const value = String(attribute.value).trim();
                await updateProductVariantAttribute(productId, variantId, {
                    id: attribute.id,
                    attribute_name: attribute.name,
                    attribute_value: value,
                    ...(attribute.colorCode ? { color_code: attribute.colorCode } : {}),
                });
                setSavedAttributes((current) => current.map((entry) => entry.clientId === attribute.clientId ? { ...entry, value, colorCode: attribute.colorCode } : entry));
                setAttributeDrafts((current) => current.map((entry) => entry.clientId === attribute.clientId ? { ...entry, value } : entry));
            }
            // Upload replacements before deleting their originals.
            for (const image of addedImages) {
                const response = await uploadProductImage(productId, image.file, { variantId });
                const uploaded = { ...normalizeImage(response?.image), clientId: image.clientId };
                if (!uploaded.id || !uploaded.url) {
                    throw new Error("The upload response is missing the saved photo. Reopen the variant to verify its photos before retrying.");
                }
                setSavedImages((current) => [...current, uploaded]);
                setGalleryImages((current) => current.map((entry) => entry.clientId === image.clientId ? uploaded : entry));
                discardPreview(image);
            }
            for (const image of deletedImages) {
                await deleteProductImage(variantId, image.id);
                setSavedImages((current) => current.filter((entry) => entry.id !== image.id));
            }
            await onVariantUpdated?.();
        } catch (error) {
            setSaveError(error?.message || "Failed to save variant changes. Unsaved changes can be retried.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteVariant = async () => {
        if (busy) return;
        if (!productId || !variantId) {
            setSaveError("A valid product and variant id are required to delete this variant.");
            return;
        }
        setIsDeleting(true);
        setSaveError("");
        try {
            await deleteProductVariant(productId, variantId);
            await onVariantUpdated?.();
            onClose?.();
        } catch (error) {
            setSaveError(error?.message || "Failed to delete variant.");
        } finally {
            setIsDeleting(false);
        }
    };

    const rawLastOrderSaleDate = stats?.last_order_sale_date ?? stats?.lastOrderSaleDate ?? stats?.LastOrderSaleDate ?? "N/A";
    const lastOrderSaleDate = typeof rawLastOrderSaleDate === "string"
        ? rawLastOrderSaleDate.trim().split(/[T\s]/)[0] || "N/A"
        : rawLastOrderSaleDate || "N/A";
    const statItems = [
        { label: "Stock Remaining", value: stats?.stock_remaining ?? stats?.StockRemaining ?? "—" },
        { label: "Total Revenue", value: stats ? formatCurrency(stats?.total_revenue_cents ?? stats?.totalRevenueCents ?? stats?.TotalRevenueCents ?? 0) : "—" },
        { label: "Last Ordered", value: lastOrderSaleDate },
        { label: "Number of Sales", value: stats?.num_of_sales ?? stats?.numOfSales ?? stats?.NumOfSales ?? "—" },
    ];

    return (
        <Modal2
            isOpen={isOpen}
            onClose={() => { if (!busy) onClose?.(); }}
            title="Variant Details"
            actionButtons={[
                {
                    key: "delete",
                    text: isDeleting ? "Deleting..." : "Delete Variant",
                    style: { backgroundColor: "var(--danger)", border: "1px solid var(--danger)" },
                    disabled: editingDisabled,
                    onClick: handleDeleteVariant,
                },
                {
                    key: "save",
                    text: isSaving ? "Saving..." : "Save Changes",
                    disabled: editingDisabled || !hasChanges,
                    onClick: handleSaveChanges,
                },
            ]}
        >
            <div className="variant-modal-content">
                <div className="variant-modal-stats-grid">
                    {statItems.map((item) => (
                        <div key={item.label} className="variant-modal-stat-card">
                            <div className="variant-modal-stat-label">{item.label}</div>
                            <div className="variant-modal-stat-value">{isLoading ? "Loading..." : item.value}</div>
                        </div>
                    ))}
                </div>
                {loadError && <div role="alert" className="variant-modal-error">{loadError}</div>}
                <div className="variant-modal-sections">
                    <div className="variant-modal-summary-grid">
                        <div>
                            <div className="variant-modal-summary-label">SKU</div>
                            <div className="variant-modal-summary-value">{variant?.sku?.display || variant?.SKU || variant?.sku || "N/A"}</div>
                        </div>
                        <Input
                            type="money"
                            label="Price"
                            min="0"
                            step="0.01"
                            value={priceDraft}
                            onChange={(event) => setPriceDraft(event.target.value)}
                            disabled={editingDisabled}
                        />
                        {/* Cost Placeholder */}
                        <Input
                            type="money"
                            label="Cost"
                            min="0"
                            step="0.01"
                            value={priceDraft}
                            onChange={(event) => setPriceDraft(event.target.value)}
                            disabled={editingDisabled}
                        />
                        {/* Weight Placeholder */}
                        <Input
                            type="number"
                            label="Weight"
                            value={priceDraft}
                            onChange={(event) => setPriceDraft(event.target.value)}
                            disabled={editingDisabled}
                        />
                        <div className="variant-modal-attributes-section">
                            <div className="variant-modal-attributes-header">Attributes</div>
                            {attributeDrafts.length === 0 ? (
                                <div className="variant-modal-empty-state">No attributes for this variant.</div>
                            ) : attributeDrafts.map((attribute) => (
                                    <Input
                                        key={attribute.clientId}
                                        label={attribute.name}
                                        type={attribute.name.trim().toLowerCase() === "color" ? "textAndColor" : "text"}
                                        value={attribute.value}
                                        onChange={(event) => setAttributeDrafts((current) => current.map((entry) => (
                                            entry.clientId === attribute.clientId ? { ...entry, value: event.target.value } : entry
                                        )))}
                                        disabled={editingDisabled}
                                        colorValue={attribute.colorCode || "#000000"}
                                        onColorChange={(event) => setAttributeDrafts((current) => current.map((entry) => (
                                            entry.clientId === attribute.clientId ? { ...entry, colorCode: event.target.value } : entry
                                        )))}
                                    />
                            ))}
                            {attributeDrafts.some((attribute) => attribute.name.trim().toLowerCase() === "color") && (
                                <p className="variant-modal-photo-hint">Color changes are applied when you save changes.</p>
                            )}
                        </div>
                    </div>
                    <div className="variant-modal-photos-section">
                        <div className="variant-modal-section-heading">Photos</div>
                        <p className="variant-modal-photo-hint">Photo changes are applied when you save changes.</p>
                        <div className="variant-modal-photo-content">
                            <div className="variant-modal-gallery-grid">
                                {isLoading ? (
                                    <div className="variant-modal-empty-state">Loading photos...</div>
                                ) : galleryImages.length === 0 ? (
                                    <div className="variant-modal-empty-state">No photos for this variant.</div>
                                ) : galleryImages.map((image) => (
                                    <div key={image.clientId} className="variant-modal-image-card">
                                        <img {...responsiveMedia(image.url, "160px")} loading="lazy" decoding="async" alt={image.alt_text || `Variant image ${image.id}`} className="variant-modal-image" />
                                        <button
                                            type="button"
                                            className="variant-modal-delete-image-button"
                                            aria-label={`Remove ${image.alt_text || `photo ${image.id}`}`}
                                            onClick={() => handleRemoveImage(image)}
                                            disabled={editingDisabled}
                                        >
                                            <LuX size={14} aria-hidden="true" />
                                        </button>
                                        <label className="variant-modal-replace-image-button">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                aria-label={`Replace ${image.alt_text || `photo ${image.id}`}`}
                                                onChange={(event) => handleReplaceImage(image, event)}
                                                disabled={editingDisabled}
                                                className="variant-modal-file-input"
                                            />
                                            Replace
                                        </label>
                                        {image.file && <span className="variant-modal-image-pending">Unsaved</span>}
                                    </div>
                                ))}
                            </div>
                            <input
                                id={uploadInputId}
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                aria-label="Choose variant photos"
                                onChange={handleSelectImages}
                                disabled={editingDisabled}
                                className="variant-modal-upload-input"
                            />
                            <button
                                type="button"
                                className="variant-modal-upload-button"
                                aria-controls={uploadInputId}
                                onClick={() => fileInputRef.current?.click()}
                                disabled={editingDisabled}
                            >
                                <LuImagePlus size={24} aria-hidden="true" />
                                Choose Photos
                            </button>
                        </div>
                    </div>
                </div>
                {saveError && <div role="alert" className="variant-modal-save-error">{saveError}</div>}
            </div>
        </Modal2>
    );
}
